import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DownloadReceiptButtonProps {
  consultationId: string;
  /** SHA-256-derived token for the "other party" flow (no auth session). */
  accessToken?: string | null;
  /** Visual variant of the button. */
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg";
  className?: string;
  /** Customize the visible label. */
  label?: string;
  /** When true, button is disabled with a tooltip-like helper text underneath. */
  disabled?: boolean;
  disabledHint?: string;
}

const FUNCTIONS_BASE = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1`;

export function DownloadReceiptButton({
  consultationId,
  accessToken,
  variant = "outline",
  size = "sm",
  className,
  label = "Télécharger le reçu (PDF)",
  disabled,
  disabledHint,
}: DownloadReceiptButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      // Forward Supabase session if available (seeker / mediator flow)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
      // Anon key required for the gateway
      headers.apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

      const res = await fetch(`${FUNCTIONS_BASE}/generate-consultation-receipt`, {
        method: "POST",
        headers,
        body: JSON.stringify({ consultationId, accessToken: accessToken ?? null }),
      });

      if (!res.ok) {
        let message = "Impossible de générer le reçu";
        try {
          const data = await res.json();
          if (data?.error) message = data.error;
        } catch { /* ignore */ }
        throw new Error(message);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recu-mediation-${consultationId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Reçu téléchargé",
        description: "Votre reçu PDF a été enregistré.",
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Erreur inconnue";
      toast({
        title: "Téléchargement impossible",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleDownload}
        disabled={isLoading || disabled}
        className="w-full"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        {label}
      </Button>
      {disabled && disabledHint && (
        <p className="mt-1 text-xs text-muted-foreground text-center">{disabledHint}</p>
      )}
    </div>
  );
}
